import { ProcessStep, MaterialLayout, ProductLine } from "../types";
import { adaptComputedCost } from "./adaptComputedCost";
import { computeMachineCost } from "./computeMachineCost";
import { computeManTimeCost } from "./computeManTimeCost";
import { computeMaterialProcessCost } from "./computeMaterialProcessCost";
import { OptimizedMachineTimes, withOptimizedMachineTimes } from "./computeOptimizedMachineTimes";
import { computeProductsCost } from "./computeProductsCost";

export type ComputeProcessPriceParams = {
  quantity: number;
  quantityInAssembly: number | null;
  reworkRatio: number | null;
  processStep: ProcessStep & {
    materialLayout: MaterialLayout | null;
    productLines: ProductLine[];
  };
  optimizedMachineTimes: OptimizedMachineTimes;
};

export const computeProcessPrice = ({
  quantity,
  quantityInAssembly,
  reworkRatio,
  processStep,
  optimizedMachineTimes,
}: ComputeProcessPriceParams): {
  partProcessPrice: number;
  partProcessCost: number;
  partProcessMarginPrice: number;
} => {
  const manTimeCost = computeManTimeCost({
    quantity,
    reworkRatio,
    processStep,
  });

  const { machineCost } = computeMachineCost({
    quantity,
    quantityInAssembly,
    reworkRatio,
    processStep: withOptimizedMachineTimes(processStep, optimizedMachineTimes),
  });

  const productsCost = computeProductsCost({
    productLines: processStep.productLines,
    quoteRequestLineQuantity: quantity,
    quantityInAssembly,
  });

  const toolingCostOrDefault = processStep.toolingCost ?? 0;

  const totalFixedCost =
    (processStep.fixedCost || 0) * quantity + (processStep.fixedCostPerSeries || 0);

  const { materialCost } = computeMaterialProcessCost({
    processStep,
    quoteRequestLineQuantity: quantity,
  });

  const computedProcessCost =
    machineCost + manTimeCost + toolingCostOrDefault + totalFixedCost + materialCost;

  const processCost = Math.max(computedProcessCost, processStep.minimumOrderAmount || 0);

  const totalQuantity = quantity * (quantityInAssembly || 1);
  const partProcessCostWithoutProducts = processCost / totalQuantity;
  const partProductsCost = productsCost / totalQuantity;

  const computedPartProcessCost = partProcessCostWithoutProducts + partProductsCost;

  const computedPartProcessMarginPrice =
    (partProcessCostWithoutProducts * (processStep.margin || 0)) / 100 +
    (partProductsCost * (processStep.productsMarginRatio || 0)) / 100;

  const {
    cost: partProcessCost,
    price: partProcessPrice,
    marginPrice: partProcessMarginPrice,
  } = adaptComputedCost({
    cost: computedPartProcessCost,
    marginPrice: computedPartProcessMarginPrice,
  });

  return {
    partProcessCost: partProcessCost,
    partProcessMarginPrice: partProcessMarginPrice,
    partProcessPrice: partProcessPrice,
  };
};
